import { required } from '@sequencehq/utils/validation'
import { FormFields, useForm } from '@sequencehq/utils'
import { useNotifications } from '@sequencehq/core-components'
import type { ServerAsset } from '@sequencehq/api'
import deepmerge from 'deepmerge'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { usePostHog } from 'posthog-js/react'

export type FeedbackType = 'feedback' | 'issue'
export type IssueSeverity = '1' | '2' | '3'

export const MAX_FEEDBACK_ATTACHMENTS = 3

// The cap is a cumulative total, so word it as such rather than the picker's default "at a time".
export const TOO_MANY_ATTACHMENTS_ERROR = `You can attach up to ${MAX_FEEDBACK_ATTACHMENTS} images.`

// A client-side uid keeps the enter/exit animations stable — uploaded assets can
// share the same server id, so the id alone isn't a reliable React key.
export type FeedbackAttachment = {
  uid: string
  asset: ServerAsset
}

type ShareFeedbackModel = {
  feedbackType: FeedbackType
  message: string
  issueSeverity: IssueSeverity | null
}

type APIFeedbackFormat = {
  type: 'feedback' | 'issue'
  message: string
  severity: '1' | '2' | '3' | '4'
}

const initialModelValue: ShareFeedbackModel = {
  feedbackType: 'feedback',
  message: '',
  issueSeverity: null
}

const modelToApi = (model: ShareFeedbackModel): APIFeedbackFormat => {
  if (model.feedbackType === 'feedback') {
    return {
      type: 'feedback',
      severity: '4',
      message: model.message
    }
  }

  if (!model.issueSeverity) {
    throw new Error('Issue severity is required')
  }

  return {
    type: 'issue',
    severity: model.issueSeverity,
    message: model.message
  }
}

export const adapters = {
  out: modelToApi
}

type UseShareFeedbackModal = (props: { onClose: () => void }) => {
  queries: {
    isSaving: boolean
    fieldsConfig: {
      feedbackType: FormFields<ShareFeedbackModel>['feedbackType']
      message: FormFields<ShareFeedbackModel>['message']
      issueSeverity: Omit<
        FormFields<ShareFeedbackModel>['issueSeverity'],
        'options'
      > & {
        options: {
          value: IssueSeverity
          label: string
          description: string
        }[]
      }
    }
    feedbackType: ShareFeedbackModel['feedbackType']
    attachments: FeedbackAttachment[]
    isUploadingAttachments: boolean
    attachmentError: string | null
    remainingAttachmentSlots: number
  }
  mutators: {
    // Returns true when the feedback was submitted, so the modal knows to close.
    saveFeedback: () => Promise<boolean>
    closeModal: () => void
    onAttachmentsPicked: () => void
    onAttachmentUploadSuccess: (asset: ServerAsset) => void
    onAttachmentUploadFailure: (error: string) => void
    onAttachmentsUploadComplete: () => void
    removeAttachment: (index: number) => void
  }
}

export const useShareFeedbackModal: UseShareFeedbackModal = props => {
  const { displayNotification } = useNotifications()
  const posthog = usePostHog()
  const [isSaving, setIsSaving] = useState(false)
  const [showValidationErrors, setShowValidationErrors] = useState(false)
  const [attachments, setAttachments] = useState<FeedbackAttachment[]>([])
  const [isUploadingAttachments, setIsUploadingAttachments] = useState(false)
  const [attachmentError, setAttachmentError] = useState<string | null>(null)

  const form = useForm<ShareFeedbackModel>({
    value: initialModelValue,
    showValidationErrors,
    fieldConfiguration: [
      {
        property: 'feedbackType',
        options: [
          {
            value: 'feedback',
            label: 'Submit feedback'
          },
          {
            value: 'issue',
            label: 'Report an issue'
          }
        ],
        validation: [required]
      },
      {
        property: 'message',
        validation: [required]
      },
      {
        property: 'issueSeverity',
        validation: [required],
        disabled: model => model.formData.feedbackType !== 'issue'
      }
    ]
  })

  const saveFeedback = useCallback(async (): Promise<boolean> => {
    if (!form.queries.isValid) {
      setShowValidationErrors(true)
      return false
    }

    setIsSaving(true)

    try {
      const sessionReplayUrl =
        posthog?.get_session_replay_url?.({
          withTimestamp: true,
          timestampLookBack: 30
        }) || undefined

      const response = await dashboardv99990101Client.postFeedback({
        body: {
          ...adapters.out(form.queries.formData),
          sessionReplayUrl,
          attachmentAssetIds: attachments.length
            ? attachments.map(attachment => attachment.asset.id)
            : undefined
        }
      })

      if (response.error) {
        throw new Error(response.res.statusText)
      }

      displayNotification('Feedback shared', {
        type: 'success',
        description:
          "Your feedback is shared with the Sequence team and they'll pick this up as soon as possible."
      })

      // The modal closes itself when saveFeedback resolves true.
      return true
    } catch {
      displayNotification('Failed to share feedback', {
        type: 'error',
        description:
          'We were unable to share your feedback. Please try again later.'
      })
      return false
    } finally {
      setIsSaving(false)
    }
  }, [
    displayNotification,
    form.queries.formData,
    form.queries.isValid,
    posthog,
    attachments
  ])

  const closeModal = useCallback(() => {
    props.onClose()
  }, [props])

  const onAttachmentsPicked = useCallback(() => {
    setAttachmentError(null)
    setIsUploadingAttachments(true)
  }, [])

  const onAttachmentUploadSuccess = useCallback((asset: ServerAsset) => {
    setAttachments(current => [...current, { uid: crypto.randomUUID(), asset }])
  }, [])

  const onAttachmentUploadFailure = useCallback((error: string) => {
    setAttachmentError(error)
  }, [])

  const onAttachmentsUploadComplete = useCallback(() => {
    setIsUploadingAttachments(false)
  }, [])

  const removeAttachment = useCallback((index: number) => {
    setAttachmentError(null)
    setAttachments(current => current.filter((_, i) => i !== index))
  }, [])

  const fieldsConfig = useMemo(() => {
    return deepmerge(form.fields, {
      issueSeverity: {
        options: [
          {
            value: '3' as IssueSeverity,
            label: 'Minor',
            description:
              "A small issue that doesn't require immediate attention"
          },
          {
            value: '2' as IssueSeverity,
            label: 'Medium',
            description: 'An annoying issue, but I can continue'
          },
          {
            value: '1' as IssueSeverity,
            label: 'Urgent',
            description: 'This is preventing me from using Sequence'
          }
        ]
      }
    })
  }, [form.fields])

  useEffect(() => {
    setShowValidationErrors(false)
  }, [form.queries.formData.feedbackType])

  return {
    queries: {
      isSaving,
      fieldsConfig,
      feedbackType: form.queries.formData.feedbackType,
      attachments,
      isUploadingAttachments,
      attachmentError,
      remainingAttachmentSlots: MAX_FEEDBACK_ATTACHMENTS - attachments.length
    },
    mutators: {
      saveFeedback,
      closeModal,
      onAttachmentsPicked,
      onAttachmentUploadSuccess,
      onAttachmentUploadFailure,
      onAttachmentsUploadComplete,
      removeAttachment
    }
  }
}
